import { z } from 'zod'
import { parseDay } from '@/features/calendar/lib/dates'
import type { Campaign, CampaignDerivedStatus, CampaignStatus } from '@/types/api'
import type { CampaignInput } from '../api'

/** Campaign dialog form ⇄ payload. Server rules (CampaignController): code unique per tenant, end ≥ start, color `#RRGGBB`. */

const optionalDate = z.string().trim().refine((v) => v === '' || parseDay(v) !== null, 'Enter a valid date')

export const campaignFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(160),
    code: z.string().trim().max(40),
    advertiser: z.string().trim().max(160),
    description: z.string().max(2000),
    start_date: optionalDate,
    end_date: optionalDate,
    priority: z.number().int().min(0).max(100),
    status: z.enum(['draft', 'active', 'paused', 'archived']),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable(),
    condition_rule_id: z.string().nullable(),
    condition_unknown_as: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.start_date && v.end_date && v.end_date < v.start_date) ctx.addIssue({ code: 'custom', path: ['end_date'], message: 'Must be on or after the start date' })
  })

export type CampaignFormValues = z.infer<typeof campaignFormSchema>

export function campaignFormDefaults(c?: Campaign | null): CampaignFormValues {
  return {
    name: c?.name ?? '',
    code: c?.code ?? '',
    advertiser: c?.advertiser ?? '',
    description: c?.description ?? '',
    start_date: c?.start_date ?? '',
    end_date: c?.end_date ?? '',
    priority: c?.priority ?? 50,
    status: c?.status ?? 'active',
    color: c?.color ?? null,
    condition_rule_id: c?.condition?.rule_id ?? null,
    condition_unknown_as: c?.condition?.unknown_as ?? false,
  }
}

const orNull = (v: string) => (v.trim() === '' ? null : v.trim())

export function campaignFormToPayload(v: CampaignFormValues): CampaignInput {
  return {
    name: v.name.trim(),
    code: orNull(v.code),
    advertiser: orNull(v.advertiser),
    description: orNull(v.description),
    start_date: orNull(v.start_date),
    end_date: orNull(v.end_date),
    priority: v.priority,
    status: v.status,
    color: v.color ? v.color.toLowerCase() : null,
    condition_rule_id: v.condition_rule_id,
    condition_unknown_as: v.condition_rule_id ? v.condition_unknown_as : false,
  }
}

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = { draft: 'Draft', active: 'Active', paused: 'Paused', archived: 'Archived' }

export const DERIVED_STATUS_META: Record<CampaignDerivedStatus, { label: string; variant: 'success' | 'warning' | 'secondary' | 'outline' | 'default' | 'offline'; hint: string }> = {
  active: { label: 'Running', variant: 'success', hint: 'Active and within its dates — its schedules play.' },
  scheduled: { label: 'Scheduled', variant: 'default', hint: 'Active, starts on its start date.' },
  completed: { label: 'Completed', variant: 'secondary', hint: 'Its end date has passed.' },
  paused: { label: 'Paused', variant: 'warning', hint: 'Paused — its schedules don’t play.' },
  draft: { label: 'Draft', variant: 'outline', hint: 'Draft — its schedules don’t play yet.' },
  archived: { label: 'Archived', variant: 'offline', hint: 'Archived — its schedules don’t play.' },
}

/** `1 Oct – 31 Oct 2026`, `From 1 Oct 2026`, `Until …`, `No dates`. */
export function campaignDates(c: Pick<Campaign, 'start_date' | 'end_date'>): string {
  const fmt = (s: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
    if (!m) return s
    return `${Number(m[3])} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1]} ${m[1]}`
  }
  if (c.start_date && c.end_date) {
    const [a, b] = [fmt(c.start_date), fmt(c.end_date)]
    return c.start_date.slice(0, 4) === c.end_date.slice(0, 4) ? `${a.replace(/ \d{4}$/, '')} – ${b}` : `${a} – ${b}`
  }
  if (c.start_date) return `From ${fmt(c.start_date)}`
  if (c.end_date) return `Until ${fmt(c.end_date)}`
  return 'No dates'
}
